import { createController, emptyOptionSearch, optionQueryAction, todayIso } from '@eo/shared';

import {
  approveTask,
  fetchClientMembers,
  fetchClientTasks,
  fetchHistory,
  fetchMeetings,
  fetchQueue,
  rejectTask,
  revertDecision,
  searchClients,
  searchTaskTypes,
} from './api/api';
import type { ApproveTaskBody, HistoryEntry, QueueAssignee, RejectTaskBody } from './api/types';
import { setupRequiredFromError } from './helpers';
import type { ReviewBoardState } from './types';

const emptyOptions = (): ReviewBoardState['options'] => ({
  clients: emptyOptionSearch(),
  taskTypes: emptyOptionSearch(),
  tasks: emptyOptionSearch(),
});

const initialState: ReviewBoardState = {
  date: todayIso(),
  tasks: [],
  history: [],
  meetings: [],
  setupRequired: null,
  membersByClient: {},
  options: emptyOptions(),
};

function removeTask(state: ReviewBoardState, taskId: string): ReviewBoardState['tasks'] {
  return state.tasks.filter((t) => t.taskId !== taskId);
}

function prependHistory(
  state: ReviewBoardState,
  action: string,
  body: { taskId: string; taskName: string; client?: string; meetingTitle?: string },
  historyId: string | null,
  actionDate: string,
  clickupTaskId: string | null,
): HistoryEntry[] {
  if (!historyId) return state.history;
  const entry: HistoryEntry = {
    id: historyId,
    action,
    actionDate,
    taskId: body.taskId,
    clickupTaskId,
    taskName: body.taskName,
    client: body.client ?? null,
    meetingTitle: body.meetingTitle ?? null,
    createdAt: new Date().toISOString(),
  };
  return [entry, ...state.history];
}

export const reviewBoardController = createController<ReviewBoardState>({
  name: 'reviewBoard',
  state: initialState,
  fetchData: async ({ queryParams }) => {
    const params = (queryParams ?? {}) as { date?: string };
    const date = params.date && /^\d{4}-\d{2}-\d{2}$/.test(params.date) ? params.date : todayIso();

    const historyPromise = fetchHistory(date);
    void historyPromise.catch(() => undefined);
    const meetingsPromise = fetchMeetings(date);
    void meetingsPromise.catch(() => undefined);

    let queue;
    try {
      queue = await fetchQueue(date);
    } catch (err) {
      const setupRequired = setupRequiredFromError(err);
      if (!setupRequired) throw err;
      return {
        date,
        tasks: [],
        history: [],
        meetings: [],
        setupRequired,
        membersByClient: {},
        options: emptyOptions(),
      };
    }

    // History stays strict — the clear-plate state is a lie without it.
    // Meetings only explain the day, so they degrade to none.
    const [history, meetings] = await Promise.all([
      historyPromise,
      meetingsPromise.catch(() => ({ meetings: [] })),
    ]);
    return {
      date,
      tasks: queue.tasks,
      history: history.history,
      meetings: meetings.meetings,
      setupRequired: null,
      membersByClient: {},
      options: emptyOptions(),
    };
  },
  asyncActions: {
    approve: {
      action: async (payload) => {
        const body = payload as ApproveTaskBody;
        const res = await approveTask(body);
        return {
          body,
          historyId: res.historyId,
          clickupTaskId: res.clickupTaskId,
          failedAttachments: res.failedAttachments ?? [],
        };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { body, historyId, clickupTaskId } = (
            action as {
              payload: { body: ApproveTaskBody; historyId: string | null; clickupTaskId: string };
            }
          ).payload;
          return {
            ...state,
            tasks: removeTask(state, body.task.taskId),
            history: prependHistory(
              state,
              'approved',
              { ...body.task, taskId: body.task.taskId },
              historyId,
              body.actionDate,
              clickupTaskId,
            ),
          };
        },
      },
    },
    reject: {
      action: async (payload) => {
        const body = payload as RejectTaskBody;
        const res = await rejectTask(body);
        return { body, historyId: res.historyId };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { body, historyId } = (
            action as { payload: { body: RejectTaskBody; historyId: string | null } }
          ).payload;
          return {
            ...state,
            tasks: removeTask(state, body.taskId),
            history: prependHistory(state, 'rejected', body, historyId, body.actionDate, null),
          };
        },
      },
    },
    // The returned task is not rebuilt from the history entry — it has to come
    // back with everything the queue knows about it, so the queue is what
    // answers.
    revert: {
      action: async (payload) => {
        const { historyId, taskId, date, confirmed } = payload as {
          historyId: string;
          taskId: string;
          date: string;
          confirmed?: boolean;
        };
        await revertDecision(confirmed ? { taskId, confirmed } : { taskId });
        return { historyId, tasks: (await fetchQueue(date)).tasks };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { historyId, tasks } = (
            action as { payload: { historyId: string; tasks: ReviewBoardState['tasks'] } }
          ).payload;
          return {
            ...state,
            tasks,
            history: state.history.filter((entry) => entry.id !== historyId),
          };
        },
      },
    },
    loadMembers: {
      action: async (payload) => {
        const { clientId } = payload as { clientId: string };
        const res = await fetchClientMembers(clientId);
        return { clientId, members: res.members };
      },
      handlers: {
        onSuccess: (state, { action }) => {
          const { clientId, members } = (
            action as { payload: { clientId: string; members: QueueAssignee[] } }
          ).payload;
          return {
            ...state,
            membersByClient: { ...state.membersByClient, [clientId]: members },
          };
        },
      },
    },
    queryOptions: optionQueryAction<ReviewBoardState>({
      clients: async (params) => {
        const { clients, pagination } = await searchClients(params);
        return { items: clients, pagination };
      },
      taskTypes: async (params) => {
        const { taskTypes, pagination } = await searchTaskTypes(params);
        return { items: taskTypes, pagination };
      },
      tasks: async ({ filters, ...params }) => {
        const { tasks, pagination } = await fetchClientTasks(String(filters?.clientId), params);
        return { items: tasks, pagination };
      },
    }),
  },
});
